/**
 * Search metadata for every public URL: used by the app to update the document head on
 * navigation, and by seo/vite-plugin.ts to prerender one HTML file per URL plus the
 * sitemap at build time.
 *
 * No imports, so the Vite config can load this file too.
 *
 * Copy rule: never put fee rates here. Every bundled preset is unverified and rates
 * change; the calculator shows rates with their verification status, search snippets
 * cannot.
 */

/** Public address of the site. Override at build time with VITE_SITE_URL. */
export const DEFAULT_SITE_URL = "https://netkeep-web.onrender.com";

export interface SeoPage {
  path: string;
  /** <title>: keyword first, under ~65 characters. */
  title: string;
  /** Meta description: what the searcher gets, under ~160 characters. */
  description: string;
  /** Visible page heading (platform pages). */
  heading?: string;
  /** Visible intro paragraph (platform pages). */
  intro?: string;
  /** Platform slug from the fee data, preselected in the calculator. */
  platformSlug?: string;
  /** False for pages that only show this browser's own data. */
  index: boolean;
}

const confirm = (name: string) =>
  `Rates come from NetKeep's fee data and show whether they have been verified. Always confirm current fees with ${name}.`;

function platformPage(slug: string, name: string, feeWords: string): SeoPage {
  return {
    path: `/${slug}-fee-calculator`,
    title: `${name} Fee Calculator: What You Net After Fees, Tax & Shipping`,
    description: `Free ${name} fee calculator: see how much you net, what you actually make, from a sale after ${name} fees, sales tax and shipping. No sign-up.`,
    heading: `${name} fee calculator`,
    intro: `Enter your sale price, item cost and shipping to see what you net: your profit and payout after ${feeWords}. Sales tax is shown separately and never counted as your income. ${confirm(name)}`,
    platformSlug: slug,
    index: true,
  };
}

export const PLATFORM_PAGES: readonly SeoPage[] = [
  platformPage("ebay", "eBay", "eBay final value fees and payment processing"),
  platformPage("mercari", "Mercari", "Mercari fees"),
  platformPage("etsy", "Etsy", "Etsy fees"),
  platformPage("facebook-marketplace", "Facebook Marketplace", "Facebook Marketplace fees"),
  platformPage("paypal", "PayPal", "PayPal Goods & Services or invoice fees"),
];

export const SEO_PAGES: readonly SeoPage[] = [
  {
    path: "/",
    title: "NetKeep: Net Profit Calculator for eBay, PayPal, Mercari & Etsy Sellers",
    description:
      "Free calculator for online sellers: see how much you net from a sale after eBay, PayPal, Mercari, Etsy and Facebook Marketplace fees, sales tax and shipping.",
    index: true,
  },
  ...PLATFORM_PAGES,
  {
    path: "/compare",
    title: "Compare Selling Fees: eBay vs Mercari vs Etsy vs Facebook",
    description:
      "Enter one sale and compare what you would net on eBay, Mercari, Etsy, Facebook Marketplace and PayPal, side by side.",
    index: true,
  },
  { path: "/terms", title: "Terms of Use · NetKeep", description: "NetKeep Terms of Use.", index: true },
  { path: "/privacy", title: "Privacy Policy · NetKeep", description: "How NetKeep handles your information.", index: true },
  {
    path: "/delete-data",
    title: "Delete your data · NetKeep",
    description: "How to delete your NetKeeps data, or ask us to delete your usage data.",
    index: true,
  },
  { path: "/saved", title: "Saved · NetKeep", description: "Calculations saved in this browser.", index: false },
  { path: "/settings", title: "Settings · NetKeep", description: "NetKeep settings.", index: false },
];

export function seoFor(pathname: string): SeoPage | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return SEO_PAGES.find((p) => p.path === path);
}
